/**
 * ReferralBonuses.jsx — Job Referral Bonus Tracker
 * Browning Electric / ElecBid
 *
 * Admin view: manage all referral bonuses (configure, approve, push to payroll)
 * Employee view: see own referral bonuses + share amounts
 *
 * Detection: nightly sync scans jobber_job_notes for "Referred by: [name]"
 * Status flow: pending → ready_for_review → approved → paid
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../lib/supabase";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

// ── Brand colours (same palette as BonusTracker) ──────────────────────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.10)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.10)",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.10)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.10)",
  purple: "#a78bfa", purpleDim: "rgba(167,139,250,0.12)",
  orange: "#f97316", orangeDim: "rgba(249,115,22,0.12)",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

const ACTIVE_EMPLOYEES = [
  "Dylan", "Colton", "Kyle", "Landon", "Morgan",
  "Jon", "Justin", "Taylor", "Ayden", "Cindy",
];

const STATUS_META = {
  active:       { label: "Active",                      color: C.muted,   bg: `${C.border}30` },
  invoiced:     { label: "Invoiced — Awaiting Payment", color: C.orange,  bg: C.orangeDim },
  client_paid:  { label: "Paid by Client",              color: C.blue,    bg: C.blueDim },
  approved:     { label: "Bonus Approved",              color: C.green,   bg: C.greenDim },
  paid:         { label: "Bonus Paid",                  color: C.accent,  bg: C.accentDim },
};

const BONUS_TYPES = [
  { value: "gross_profit_pct", label: "% of Gross Profit" },
  { value: "revenue_pct",      label: "% of Revenue" },
  { value: "flat_amount",      label: "Flat Amount ($)" },
];

// ── Shared UI atoms ───────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "Unknown", color: C.muted, bg: `${C.border}30` };
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: meta.bg, color: meta.color,
      whiteSpace: "nowrap",
    }}>
      {meta.label}
    </span>
  );
}

function TypePill({ type }) {
  const meta = type === "job_sale"
    ? { label: "Job Sale", color: C.orange, bg: C.orangeDim }
    : { label: "Client Ref", color: C.blue, bg: C.blueDim };
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: meta.bg, color: meta.color,
      whiteSpace: "nowrap",
    }}>
      {meta.label}
    </span>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "7px 10px", borderRadius: 7,
  border: `1px solid ${C.border}`, background: C.card,
  color: C.text, fontSize: 13,
};

function btn(variant = "primary", extra = {}) {
  const styles = {
    primary:   { background: C.accent,   color: "#0d0f12" },
    secondary: { background: C.surface,  color: C.muted },
    danger:    { background: C.redDim,   color: C.red },
    success:   { background: C.greenDim, color: C.green },
    warning:   { background: C.yellowDim, color: C.yellow },
  };
  return {
    padding: "7px 14px", borderRadius: 7, border: "none",
    cursor: "pointer", fontSize: 12, fontWeight: 600,
    transition: "opacity 0.15s",
    ...(styles[variant] || styles.primary),
    ...extra,
  };
}

function fmt(n) {
  if (n == null || n === "") return "—";
  return `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// ── Referral detail / edit panel ──────────────────────────────────────────────
function ReferralDetailPanel({ referral, onSave, onApprove, onUnapprove, onClose, saving, userEmail }) {
  const [bonusType, setBonusType]     = useState(referral.bonus_type || "");
  const [bonusValue, setBonusValue]   = useState(referral.bonus_value != null ? String(referral.bonus_value) : "");
  const [employees, setEmployees]     = useState(
    referral.linked_employees?.length
      ? referral.linked_employees.map(e => ({ ...e }))
      : []
  );
  const [approvalNotes, setApprovalNotes] = useState(referral.approval_notes || "");
  const [addEmpName, setAddEmpName]   = useState("");
  const [dirty, setDirty]             = useState(false);

  // Recalculate share amounts when bonus changes
  const calculated = useMemo(() => {
    const rev  = Number(referral.gross_revenue)  || 0;
    const prof = Number(referral.gross_profit)   || 0;
    const val  = Number(bonusValue) || 0;
    if (!bonusType || !val) return null;
    if (bonusType === "gross_profit_pct") return +(prof * val / 100).toFixed(2);
    if (bonusType === "revenue_pct")      return +(rev  * val / 100).toFixed(2);
    if (bonusType === "flat_amount")      return val;
    return null;
  }, [bonusType, bonusValue, referral]);

  const totalSharePct = employees.reduce((s, e) => s + (Number(e.share_pct) || 0), 0);

  function updateEmpShare(idx, pct) {
    setEmployees(prev => prev.map((e, i) => i === idx ? { ...e, share_pct: Number(pct) || 0 } : e));
    setDirty(true);
  }

  function removeEmp(idx) {
    const updated = employees.filter((_, i) => i !== idx);
    // Redistribute to even split
    const n = updated.length;
    if (n) {
      const per = Math.floor(100 / n);
      const rem = 100 - per * n;
      updated.forEach((e, i) => { e.share_pct = i === 0 ? per + rem : per; });
    }
    setEmployees(updated);
    setDirty(true);
  }

  function addEmployee() {
    const name = addEmpName.trim();
    if (!name || employees.some(e => e.name.toLowerCase() === name.toLowerCase())) return;
    const n = employees.length + 1;
    const per = Math.floor(100 / n);
    const rem = 100 - per * n;
    const updated = [...employees.map((e, i) => ({ ...e, share_pct: i === 0 ? per + rem : per })), {
      name, share_pct: per, manually_added: true, share_amount: null,
    }];
    setEmployees(updated);
    setAddEmpName("");
    setDirty(true);
  }

  function handleSave() {
    const shareAmts = employees.map(e => ({
      ...e,
      share_amount: calculated != null ? +((calculated * e.share_pct) / 100).toFixed(2) : null,
    }));
    onSave(referral.id, {
      bonus_type:        bonusType || null,
      bonus_value:       bonusValue !== "" ? Number(bonusValue) : null,
      calculated_amount: calculated,
      linked_employees:  shareAmts,
      approval_notes:    approvalNotes || null,
    });
    setDirty(false);
  }

  const isApproved = referral.status === "approved";
  const isPaid     = referral.status === "paid";
  const locked     = isPaid || !onSave;

  return (
    <div style={{ background: C.card, padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", color: C.text }}>
            Job #{referral.job_number} — {referral.client_name || "Unknown Client"}
          </div>
          {referral.job_title && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{referral.job_title}</div>
          )}
          <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <StatusPill status={referral.status} />
            <TypePill type={referral.referral_type || "client_referral"} />
          </div>
        </div>
        <button onClick={onClose} style={btn("secondary", { padding: "5px 12px", fontSize: 12 })}>Close</button>
      </div>

      {/* Referral source */}
      <div>
        <SectionLabel>Referral Source</SectionLabel>
        <div style={{ fontSize: 13, color: C.text, background: C.surface, borderRadius: 8, padding: "8px 12px" }}>
          {referral.referral_source || "—"}
          <span style={{ marginLeft: 8, fontSize: 11, color: C.muted }}>
            ({referral.referral_type === "job_sale" ? "manually assigned" : referral.detected_from === "client_custom_field" ? "Jobber client field" : referral.detected_from || "sync"})
          </span>
        </div>
      </div>

      {/* Financials */}
      <div>
        <SectionLabel>Job Financials (updates nightly)</SectionLabel>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "Revenue",   val: referral.gross_revenue,  color: C.blue },
            { label: "Expenses",  val: referral.gross_expenses, color: C.red },
            { label: "Gross Profit", val: referral.gross_profit, color: Number(referral.gross_profit) >= 0 ? C.green : C.red },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ background: C.surface, borderRadius: 8, padding: "8px 14px", flex: 1, minWidth: 100 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color }}>{fmt(val)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bonus configuration */}
      {!locked && (
        <div>
          <SectionLabel>Bonus Calculation</SectionLabel>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select
              value={bonusType}
              onChange={e => { setBonusType(e.target.value); setDirty(true); }}
              style={{ ...inputStyle, flex: 2, minWidth: 180, cursor: "pointer" }}
            >
              <option value="">— Select method —</option>
              {BONUS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              type="number" min="0" step={bonusType === "flat_amount" ? "50" : "0.5"}
              placeholder={bonusType === "flat_amount" ? "Amount ($)" : "Percentage"}
              value={bonusValue}
              onChange={e => { setBonusValue(e.target.value); setDirty(true); }}
              style={{ ...inputStyle, flex: 1, minWidth: 120 }}
            />
          </div>
          {calculated != null && (
            <div style={{ marginTop: 8, padding: "8px 12px", background: C.accentDim, borderRadius: 8, fontSize: 13, color: C.accent, fontWeight: 600 }}>
              Total bonus pool: {fmt(calculated)}
            </div>
          )}
        </div>
      )}
      {locked && referral.bonus_type && (
        <div>
          <SectionLabel>Bonus Calculation</SectionLabel>
          <div style={{ fontSize: 13, color: C.text, background: C.surface, borderRadius: 8, padding: "8px 12px" }}>
            {BONUS_TYPES.find(t => t.value === referral.bonus_type)?.label} — {referral.bonus_value}{referral.bonus_type === "flat_amount" ? "" : "%"}
            {" → "}<span style={{ color: C.accent, fontWeight: 700 }}>{fmt(referral.calculated_amount)}</span> total
          </div>
        </div>
      )}

      {/* Employee split */}
      <div>
        <SectionLabel>Linked Employees & Split</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {employees.map((emp, idx) => {
            const shareAmt = calculated != null ? ((calculated * emp.share_pct) / 100) : null;
            return (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 8,
                background: C.surface, borderRadius: 8, padding: "8px 12px",
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text, flex: 2 }}>
                  {emp.name}
                  {emp.manually_added && <span style={{ marginLeft: 6, fontSize: 10, color: C.muted }}>(manually added)</span>}
                </div>
                {!locked ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }}>
                    <input
                      type="number" min="0" max="100" step="5"
                      value={emp.share_pct}
                      onChange={e => updateEmpShare(idx, e.target.value)}
                      style={{ ...inputStyle, width: 70, textAlign: "right" }}
                    />
                    <span style={{ fontSize: 12, color: C.muted }}>%</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: C.muted, flex: 1, textAlign: "right" }}>{emp.share_pct}%</div>
                )}
                <div style={{ fontSize: 14, fontWeight: 700, color: C.green, minWidth: 80, textAlign: "right" }}>
                  {shareAmt != null ? fmt(shareAmt) : "—"}
                </div>
                {!locked && employees.length > 1 && (
                  <button onClick={() => removeEmp(idx)} style={btn("danger", { padding: "3px 8px", fontSize: 11 })}>×</button>
                )}
              </div>
            );
          })}

          {/* Share percentage warning */}
          {totalSharePct !== 100 && employees.length > 0 && (
            <div style={{ fontSize: 11, color: C.yellow, padding: "4px 8px" }}>
              Shares sum to {totalSharePct}% — should equal 100%
            </div>
          )}

          {/* Add employee */}
          {!locked && (
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <select
                value={addEmpName}
                onChange={e => setAddEmpName(e.target.value)}
                style={{ ...inputStyle, flex: 2, cursor: "pointer" }}
              >
                <option value="">+ Link another employee…</option>
                {ACTIVE_EMPLOYEES.filter(e => !employees.some(ex => ex.name === e)).map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
              <button
                onClick={addEmployee}
                disabled={!addEmpName}
                style={btn("secondary", { opacity: addEmpName ? 1 : 0.4 })}
              >
                Add
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Approval notes */}
      {!locked && (
        <div>
          <SectionLabel>Approval Notes</SectionLabel>
          <textarea
            rows={2} placeholder="Expense verification notes, context for approval…"
            value={approvalNotes}
            onChange={e => { setApprovalNotes(e.target.value); setDirty(true); }}
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>
      )}
      {isApproved && referral.approval_notes && (
        <div>
          <SectionLabel>Approval Notes</SectionLabel>
          <div style={{ fontSize: 12, color: C.muted, background: C.surface, borderRadius: 8, padding: "8px 12px", lineHeight: 1.5 }}>
            {referral.approval_notes}
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            Approved by {referral.approved_by || "owner"} on {referral.approved_at ? new Date(referral.approved_at).toLocaleDateString("en-CA") : "—"}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!locked && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {dirty && (
            <button onClick={handleSave} disabled={saving} style={btn("primary", { opacity: saving ? 0.6 : 1 })}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          )}
          {referral.status === "client_paid" && !dirty && (
            <button
              onClick={() => onApprove(referral.id, approvalNotes)}
              disabled={saving}
              style={btn("success")}
            >
              Approve &amp; Lock Bonus
            </button>
          )}
          {isApproved && (
            <button onClick={() => onUnapprove(referral.id)} disabled={saving} style={btn("warning")}>
              Un-approve
            </button>
          )}
          {!dirty && referral.status === "active" && (
            <div style={{ fontSize: 11, color: C.muted, padding: "7px 0", lineHeight: 1.5 }}>
              Waiting for job to be invoiced
            </div>
          )}
          {!dirty && referral.status === "invoiced" && (
            <div style={{ fontSize: 11, color: C.orange, padding: "7px 0", lineHeight: 1.5 }}>
              Invoice sent — waiting for client payment
            </div>
          )}
        </div>
      )}
      {isPaid && (
        <div style={{ fontSize: 12, color: C.accent, background: C.accentDim, borderRadius: 8, padding: "10px 14px" }}>
          Paid on {referral.paid_at ? new Date(referral.paid_at).toLocaleDateString("en-CA") : "—"}
          {referral.payroll_period && ` — Payroll period: ${referral.payroll_period}`}
        </div>
      )}
    </div>
  );
}

// ── Push to Payroll modal ─────────────────────────────────────────────────────
function PayrollModal({ approved, onPush, onClose, pushing }) {
  const [period, setPeriod]     = useState("");
  const [selected, setSelected] = useState(new Set(approved.map(r => r.id)));

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectedReferrals = approved.filter(r => selected.has(r.id));
  const grandTotal = selectedReferrals.reduce((s, r) => s + (Number(r.calculated_amount) || 0), 0);

  // Build per-employee payroll breakdown
  const byEmployee = {};
  for (const ref of selectedReferrals) {
    for (const emp of (ref.linked_employees || [])) {
      if (!byEmployee[emp.name]) byEmployee[emp.name] = [];
      byEmployee[emp.name].push({
        job: `Job #${ref.job_number} (${ref.client_name || "client"})`,
        amount: emp.share_amount ?? (ref.calculated_amount != null ? ((ref.calculated_amount * emp.share_pct) / 100) : null),
      });
    }
  }

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
        padding: 24, width: "100%", maxWidth: 600, maxHeight: "90vh", overflowY: "auto",
        display: "flex", flexDirection: "column", gap: 20,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", color: C.accent }}>
            Push to Payroll
          </div>
          <button onClick={onClose} style={btn("secondary", { padding: "5px 12px" })}>Cancel</button>
        </div>

        {/* QB integration info */}
        <div style={{ background: C.greenDim, border: `1px solid ${C.green}30`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: C.green }}>
          Pushing will create QuickBooks Journal Entries and email Cindy (payroll) for each employee. Bonuses will be marked as paid in ElecBid.
        </div>

        {/* Payroll period */}
        <div>
          <SectionLabel>Payroll Period</SectionLabel>
          <input
            type="text" placeholder="e.g. 2026-04 or 2026-Q2"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Select referrals to push */}
        <div>
          <SectionLabel>Select Referrals to Include</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {approved.map(ref => (
              <label key={ref.id} style={{
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                background: selected.has(ref.id) ? C.greenDim : C.card,
                border: `1px solid ${selected.has(ref.id) ? C.green + "40" : C.border}`,
                borderRadius: 8, padding: "8px 12px",
              }}>
                <input
                  type="checkbox" checked={selected.has(ref.id)}
                  onChange={() => toggleSelect(ref.id)}
                  style={{ accentColor: C.accent }}
                />
                <div style={{ flex: 1, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: C.text }}>Job #{ref.job_number}</span>
                  <span style={{ color: C.muted, marginLeft: 8 }}>{ref.client_name}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{fmt(ref.calculated_amount)}</div>
              </label>
            ))}
          </div>
        </div>

        {/* Per-employee breakdown */}
        {Object.keys(byEmployee).length > 0 && (
          <div>
            <SectionLabel>Paycheck Breakdown by Employee</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {Object.entries(byEmployee).map(([name, items]) => {
                const empTotal = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
                return (
                  <div key={name} style={{ background: C.card, borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{name}</span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: C.green }}>{fmt(empTotal)}</span>
                    </div>
                    {items.map((item, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted, paddingLeft: 8 }}>
                        <span>Referral bonus — {item.job}</span>
                        <span>{fmt(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, textAlign: "right", fontSize: 14, fontWeight: 700, color: C.accent }}>
              Grand total: {fmt(grandTotal)}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btn("secondary")}>Cancel</button>
          <button
            onClick={() => onPush([...selected], period, byEmployee)}
            disabled={pushing || !selected.size || !period.trim()}
            style={btn("primary", { opacity: (pushing || !selected.size || !period.trim()) ? 0.5 : 1 })}
          >
            {pushing ? "Pushing to QB…" : `Push to QB & Mark Paid (${selected.size})`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ReferralBonuses() {
  const { session } = useSession();
  const userEmail = session?.user?.email?.toLowerCase() || "";
  const { isAdmin, hasPermission, jobberEmployeeName } = usePermissions();
  const canEdit = isAdmin || hasPermission("edit_bonus");

  const [referrals, setReferrals]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [scanning, setScanning]         = useState(false);
  const [saving, setSaving]             = useState(false);
  const [pushing, setPushing]           = useState(false);
  const [activeId, setActiveId]         = useState(null);
  const [showPayroll, setShowPayroll]   = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter]     = useState("all"); // "all" | "client_referral" | "job_sale"
  const [dateFilter, setDateFilter]     = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }); // YYYY-MM | YYYY-Qn | "all"
  const [toast, setToast]               = useState(null);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Load referrals ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("referral_bonuses")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      // Non-admin: only show own referrals
      // (filtering done client-side since linked_employees is JSONB)
      const { data, error } = await query;
      if (error) throw error;
      if (isAdmin) {
        setReferrals(data || []);
      } else {
        const myName = jobberEmployeeName?.toLowerCase();
        setReferrals(
          (data || []).filter(r =>
            (r.linked_employees || []).some(e => e.name?.toLowerCase() === myName)
          )
        );
      }
    } catch (e) {
      showToast(e.message || "Failed to load referrals", false);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, jobberEmployeeName]);

  useEffect(() => { load(); }, [load]);

  // ── Sync referrals from Jobber (salesperson field + client "Referred By") ──
  async function handleScan() {
    setScanning(true);
    try {
      const res = await apiFetch("/api/jobber-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "syncReferrals" }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Sync failed");
      const r = json.result || {};
      const newCount = (r.jobSaleCreated ?? 0) + (r.clientRefCreated ?? 0);
      showToast(`Sync complete — ${newCount} new referrals, ${r.profitUpdated ?? 0} records updated`);
      await load();
    } catch (e) {
      showToast(e.message || "Sync failed", false);
    } finally {
      setScanning(false);
    }
  }

  // ── Save changes ───────────────────────────────────────────────────────────
  async function handleSave(id, updates) {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("referral_bonuses")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      showToast("Saved");
      await load();
    } catch (e) {
      showToast(e.message || "Save failed", false);
    } finally {
      setSaving(false);
    }
  }

  // ── Approve ────────────────────────────────────────────────────────────────
  async function handleApprove(id, notes) {
    setSaving(true);
    try {
      const res = await apiFetch("/api/referral-bonuses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve", id, approved_by: userEmail, notes }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Approve failed");
      showToast("Approved and locked");
      setActiveId(null);
      await load();
    } catch (e) {
      showToast(e.message || "Approve failed", false);
    } finally {
      setSaving(false);
    }
  }

  // ── Unapprove ──────────────────────────────────────────────────────────────
  async function handleUnapprove(id) {
    setSaving(true);
    try {
      const res = await apiFetch("/api/referral-bonuses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "unapprove", id }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Failed");
      showToast("Reverted to Paid by Client");
      setActiveId(null);
      await load();
    } catch (e) {
      showToast(e.message || "Failed", false);
    } finally {
      setSaving(false);
    }
  }

  // ── Push to payroll ────────────────────────────────────────────────────────
  async function handlePushPayroll(ids, payroll_period, byEmployee) {
    setPushing(true);
    try {
      // Mark paid in Supabase
      const res = await apiFetch("/api/referral-bonuses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "markPaid", ids, payroll_period, pushed_by: userEmail }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Push failed");

      // Push one QB Journal Entry per employee (best-effort)
      const qbResults = [];
      for (const [empName, items] of Object.entries(byEmployee || {})) {
        const empTotal = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
        if (empTotal <= 0) continue;
        try {
          const qbRes = await apiFetch("/api/quickbooks-bonus", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action:       "pushBonus",
              employeeName: empName,
              bonusAmount:  empTotal,
              bonusMonth:   payroll_period,
              description:  `Referral bonus — ${payroll_period}`,
              approvedBy:   userEmail,
              bonusType:    "referral",
            }),
          });
          const qbJson = await qbRes.json();
          qbResults.push({ employee: empName, ...qbJson });
        } catch (e) {
          qbResults.push({ employee: empName, ok: false, error: e.message });
        }
      }

      const qbOk   = qbResults.filter(r => r.ok).length;
      const qbFail = qbResults.filter(r => !r.ok).length;
      let msg = `${json.pushed} bonus${json.pushed !== 1 ? "es" : ""} paid for ${payroll_period}`;
      if (qbOk > 0)   msg += ` · ${qbOk} QB journal entr${qbOk !== 1 ? "ies" : "y"} created`;
      if (qbFail > 0) msg += ` · ${qbFail} QB push failed`;
      showToast(msg, qbFail === 0);
      setShowPayroll(false);
      await load();
    } catch (e) {
      showToast(e.message || "Push failed", false);
    } finally {
      setPushing(false);
    }
  }

  // ── Date filter options ────────────────────────────────────────────────────
  const dateOptions = useMemo(() => {
    const opts = [{ value: "all", label: "All Time", group: null }];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      opts.push({ value: val, label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }), group: "month" });
    }
    [now.getFullYear(), now.getFullYear() - 1].forEach(yr => {
      [4, 3, 2, 1].forEach(q => {
        opts.push({ value: `${yr}-Q${q}`, label: `Q${q} ${yr}`, group: "quarter" });
      });
    });
    return opts;
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = referrals;
    if (statusFilter !== "all") result = result.filter(r => r.status === statusFilter);
    if (typeFilter !== "all") result = result.filter(r => r.referral_type === typeFilter);
    if (dateFilter !== "all") {
      if (dateFilter.includes("-Q")) {
        const [year, q] = dateFilter.split("-Q");
        const qMonths = { "1": [0,1,2], "2": [3,4,5], "3": [6,7,8], "4": [9,10,11] };
        const months = qMonths[q] || [];
        result = result.filter(r => {
          const d = r.job_created_at ? new Date(r.job_created_at) : null;
          return d && d.getFullYear() === Number(year) && months.includes(d.getMonth());
        });
      } else {
        const [year, month] = dateFilter.split("-");
        result = result.filter(r => {
          const d = r.job_created_at ? new Date(r.job_created_at) : null;
          return d && d.getFullYear() === Number(year) && (d.getMonth() + 1) === Number(month);
        });
      }
    }
    return result;
  }, [referrals, statusFilter, typeFilter, dateFilter]);

  const approvedReferrals = referrals.filter(r => r.status === "approved");

  const statusCounts = useMemo(() => {
    const counts = { active: 0, invoiced: 0, client_paid: 0, approved: 0, paid: 0 };
    referrals.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  }, [referrals]);

  const typeCounts = useMemo(() => {
    const counts = { client_referral: 0, job_sale: 0 };
    referrals.forEach(r => { if (counts[r.referral_type] !== undefined) counts[r.referral_type]++; });
    return counts;
  }, [referrals]);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const pendingPool   = referrals.filter(r => r.status !== "paid").reduce((s, r) => s + (Number(r.calculated_amount) || 0), 0);
  const approvedPool  = approvedReferrals.reduce((s, r) => s + (Number(r.calculated_amount) || 0), 0);

  const tabBtn = (t, label, count) => ({
    style: {
      padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
      fontSize: 12, fontWeight: 600,
      background: statusFilter === t ? (STATUS_META[t]?.bg || C.accentDim) : C.surface,
      color: statusFilter === t ? (STATUS_META[t]?.color || C.accent) : C.muted,
    },
    onClick: () => setStatusFilter(t),
    children: `${label}${count !== undefined ? ` (${count})` : ""}`,
  });

  return (
    <div style={{ color: C.text, fontFamily: "'IBM Plex Sans', 'Roboto', sans-serif" }}>
      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 8000,
          padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          background: toast.ok ? C.greenDim : C.redDim,
          border: `1px solid ${toast.ok ? C.green : C.red}40`,
          color: toast.ok ? C.green : C.red,
          boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
        }}>
          {toast.ok ? "✓ " : "✗ "}{toast.msg}
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
            Referral Bonuses
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            Auto-detected from Jobber: salesperson field (job sales) + "Referred By" client field (client referrals).
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {approvedReferrals.length > 0 && (
              <button onClick={() => setShowPayroll(true)} style={btn("success")}>
                Push to Payroll ({approvedReferrals.length})
              </button>
            )}
            <button onClick={handleScan} disabled={scanning} style={btn("secondary", { opacity: scanning ? 0.6 : 1 })}>
              {scanning ? "Syncing…" : "Sync Now"}
            </button>
          </div>
        )}
      </div>

      {/* ── Summary stats (admin only) ── */}
      {isAdmin && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          {[
            { label: "Active Bonus Pool", val: pendingPool, color: C.blue },
            { label: "Ready to Pay", val: approvedPool, color: C.green },
            { label: "Total Referrals", val: referrals.length, isCnt: true, color: C.accent },
          ].map(({ label, val, color, isCnt }) => (
            <div key={label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 18px", flex: 1, minWidth: 130 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>
                {isCnt ? val : fmt(val)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Date filter ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
          Job Created
        </span>
        <select
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          style={{ ...inputStyle, width: "auto", minWidth: 200, cursor: "pointer", fontSize: 12 }}
        >
          {dateOptions.filter(o => !o.group).map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
          <optgroup label="By Month">
            {dateOptions.filter(o => o.group === "month").map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
          <optgroup label="By Quarter">
            {dateOptions.filter(o => o.group === "quarter").map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
        </select>
        {dateFilter !== "all" && (
          <button onClick={() => setDateFilter("all")} style={btn("secondary", { padding: "5px 10px", fontSize: 11 })}>
            Clear
          </button>
        )}
      </div>

      {/* ── Status filter tabs ── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        <button {...tabBtn("all", "All", referrals.length)} />
        <button {...tabBtn("active", "Active", statusCounts.active)} />
        <button {...tabBtn("invoiced", "Invoiced", statusCounts.invoiced)} />
        <button {...tabBtn("client_paid", "Paid by Client", statusCounts.client_paid)} />
        <button {...tabBtn("approved", "Bonus Approved", statusCounts.approved)} />
        <button {...tabBtn("paid", "Bonus Paid", statusCounts.paid)} />
      </div>

      {/* ── Type filter buttons ── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {[
          { value: "all",              label: "All Types",        color: C.accent,  bg: C.accentDim },
          { value: "client_referral",  label: `Client Referrals (${typeCounts.client_referral})`, color: C.blue,   bg: C.blueDim },
          { value: "job_sale",         label: `Job Sales (${typeCounts.job_sale})`,               color: C.orange, bg: C.orangeDim },
        ].map(({ value, label, color, bg }) => (
          <button
            key={value}
            onClick={() => setTypeFilter(value)}
            style={{
              padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600,
              background: typeFilter === value ? bg : C.surface,
              color: typeFilter === value ? color : C.muted,
              transition: "background 0.15s, color 0.15s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Referrals list ── */}
      {loading ? (
        <div style={{ textAlign: "center", color: C.muted, padding: 40, fontSize: 13 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: C.muted, padding: 40 }}>
          <div style={{ fontSize: 32, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", color: C.accent, marginBottom: 8 }}>
            No Referrals
          </div>
          <div style={{ fontSize: 13, maxWidth: 380, margin: "0 auto", lineHeight: 1.6 }}>
            {isAdmin
              ? `Set "Referred By" on a Jobber client or assign a Salesperson on a Jobber job — both are auto-detected on nightly sync.`
              : "No referral bonuses linked to your account yet."}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(ref => {
            const isActive = ref.id === activeId;
            const empList = (ref.linked_employees || []).map(e => e.name).join(", ");
            return (
              <div key={ref.id}>
                {/* ── Card row ── */}
                <div
                  onClick={() => setActiveId(isActive ? null : ref.id)}
                  style={{
                    background: isActive ? `${C.accent}08` : C.surface,
                    border: `1px solid ${isActive ? C.accent + "50" : C.border}`,
                    borderRadius: isActive ? "12px 12px 0 0" : 12,
                    padding: "14px 16px", cursor: "pointer",
                    display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
                    transition: "border-color 0.15s, border-radius 0.1s",
                  }}
                >
                  {/* Job info */}
                  <div style={{ flex: 2, minWidth: 160 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                      Job #{ref.job_number}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {ref.client_name || "—"}
                      {ref.job_title && <span style={{ marginLeft: 8 }}>· {ref.job_title}</span>}
                    </div>
                  </div>

                  {/* Type */}
                  <div style={{ minWidth: 90 }}>
                    <TypePill type={ref.referral_type || "client_referral"} />
                  </div>

                  {/* Referral source */}
                  <div style={{ flex: 2, minWidth: 120 }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>
                      {ref.referral_type === "job_sale" ? "Salesman" : "Referred by"}
                    </div>
                    <div style={{ fontSize: 13, color: C.text }}>{empList || "—"}</div>
                  </div>

                  {/* Bonus amount */}
                  <div style={{ minWidth: 90, textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Bonus Pool</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: ref.calculated_amount ? C.green : C.dim }}>
                      {ref.calculated_amount != null ? fmt(ref.calculated_amount) : ref.bonus_type ? "Calc…" : "Not set"}
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{ minWidth: 120 }}>
                    <StatusPill status={ref.status} />
                  </div>

                  {/* Expand chevron */}
                  <div style={{ fontSize: 14, color: C.muted, transition: "transform 0.2s", transform: isActive ? "rotate(180deg)" : "rotate(0deg)" }}>
                    ▾
                  </div>
                </div>

                {/* ── Inline detail drawer ── */}
                {isActive && (
                  <div style={{
                    border: `1px solid ${C.accent}50`,
                    borderTop: "none",
                    borderRadius: "0 0 12px 12px",
                    overflow: "hidden",
                  }}>
                    <ReferralDetailPanel
                      referral={ref}
                      onSave={canEdit ? handleSave : undefined}
                      onApprove={canEdit ? handleApprove : undefined}
                      onUnapprove={canEdit ? handleUnapprove : undefined}
                      onClose={() => setActiveId(null)}
                      saving={saving}
                      userEmail={userEmail}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Payroll modal ── */}
      {showPayroll && (
        <PayrollModal
          approved={approvedReferrals}
          onPush={handlePushPayroll}
          onClose={() => setShowPayroll(false)}
          pushing={pushing}
        />
      )}
    </div>
  );
}

// ── Accordion group wrapper ───────────────────────────────────────────────────
// ── Compact per-employee view for BonusTracker cards ─────────────────────────
// Exported separately so BonusTracker can embed it without the full admin UI.
// Pass onRefresh so saves/approvals trigger a data reload in BonusTracker.
export function EmployeeReferralSection({ employeeName, referrals, onRefresh }) {
  const { session } = useSession();
  const userEmail = session?.user?.email?.toLowerCase() || "";
  const { isAdmin, hasPermission } = usePermissions();
  const canEdit = isAdmin || hasPermission("edit_bonus");

  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [drawerType, setDrawerType] = useState(null); // 'referrals' | 'sales'
  const [search, setSearch]     = useState("");
  const [toast, setToast]       = useState(null);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  const myReferrals = useMemo(() => {
    const nameLower = (employeeName || "").toLowerCase();
    return referrals.filter(r =>
      (r.linked_employees || []).some(e => e.name?.toLowerCase() === nameLower)
    );
  }, [employeeName, referrals]);

  const clientReferrals = useMemo(
    () => myReferrals.filter(r => (r.referral_type || "client_referral") === "client_referral"),
    [myReferrals]
  );
  const jobSales = useMemo(
    () => myReferrals.filter(r => r.referral_type === "job_sale"),
    [myReferrals]
  );

  function openDrawer(type) {
    setDrawerType(type);
    setSearch("");
    setActiveId(null);
  }

  function closeDrawer() {
    setDrawerType(null);
    setSearch("");
    setActiveId(null);
  }

  async function handleSave(id, updates) {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("referral_bonuses")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      showToast("Saved");
      onRefresh?.();
    } catch (e) {
      showToast(e.message || "Save failed", false);
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(id, notes) {
    setSaving(true);
    try {
      const res = await apiFetch("/api/referral-bonuses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve", id, approved_by: userEmail, notes }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Approve failed");
      showToast("Approved and locked");
      setActiveId(null);
      onRefresh?.();
    } catch (e) {
      showToast(e.message || "Approve failed", false);
    } finally {
      setSaving(false);
    }
  }

  async function handleUnapprove(id) {
    setSaving(true);
    try {
      const res = await apiFetch("/api/referral-bonuses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "unapprove", id }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Failed");
      showToast("Reverted to Paid by Client");
      setActiveId(null);
      onRefresh?.();
    } catch (e) {
      showToast(e.message || "Failed", false);
    } finally {
      setSaving(false);
    }
  }

  if (!myReferrals.length) {
    return (
      <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>No referral bonuses</div>
    );
  }

  const pendingTotal = myReferrals.filter(r => r.status !== "paid").reduce((s, r) => {
    const myShare = (r.linked_employees || []).find(
      e => e.name?.toLowerCase() === (employeeName || "").toLowerCase()
    );
    return s + (Number(myShare?.share_amount) || 0);
  }, 0);

  function renderRow(item) {
    const isActive = item.id === activeId;
    const myShare = (item.linked_employees || []).find(
      e => e.name?.toLowerCase() === (employeeName || "").toLowerCase()
    );
    const myAmount = myShare?.share_amount;
    const myPct    = myShare?.share_pct;
    return (
      <div key={item.id}>
        <div
          onClick={() => setActiveId(isActive ? null : item.id)}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: isActive
              ? `${C.accent}10`
              : item.status === "paid" ? C.accentDim
              : item.status === "approved" ? C.greenDim
              : item.status === "client_paid" ? C.blueDim
              : item.status === "invoiced" ? C.orangeDim
              : `${C.border}15`,
            padding: "9px 12px", cursor: "pointer",
            borderBottom: `1px solid ${C.border}20`,
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
              Job #{item.job_number} — {item.client_name || "client"}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
              {myPct != null && myPct !== 100
                ? `Your share: ${myPct}%`
                : item.referral_type === "job_sale" ? "Your sale" : "Your referral"}
              {item.status === "paid" && item.payroll_period && ` · Paid ${item.payroll_period}`}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: 14, fontWeight: 700,
                color: item.status === "paid" ? C.accent : myAmount != null ? C.green : C.dim,
              }}>
                {myAmount != null ? fmt(myAmount) : item.calculated_amount == null ? "—" : "Pending"}
              </div>
              <StatusPill status={item.status} />
            </div>
            <div style={{
              display: "inline-block", fontSize: 11, color: C.muted,
              transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}>▾</div>
          </div>
        </div>
        {isActive && (
          <div style={{ borderTop: `1px solid ${C.accent}30` }}>
            <ReferralDetailPanel
              referral={item}
              onSave={canEdit ? handleSave : undefined}
              onApprove={canEdit ? handleApprove : undefined}
              onUnapprove={canEdit ? handleUnapprove : undefined}
              onClose={() => setActiveId(null)}
              saving={saving}
              userEmail={userEmail}
            />
          </div>
        )}
      </div>
    );
  }

  const drawerList = drawerType === "referrals" ? clientReferrals : drawerType === "sales" ? jobSales : [];
  const q = search.toLowerCase().trim();
  const filteredList = q
    ? drawerList.filter(r =>
        String(r.job_number || "").toLowerCase().includes(q) ||
        (r.client_name || "").toLowerCase().includes(q) ||
        (r.job_title || "").toLowerCase().includes(q)
      )
    : drawerList;

  const drawerTitle = drawerType === "referrals"
    ? `${employeeName} — Client Referrals`
    : `${employeeName} — Job Sales`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {toast && (
        <div style={{
          padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: toast.ok ? C.greenDim : C.redDim,
          color: toast.ok ? C.green : C.red, marginBottom: 4,
        }}>
          {toast.ok ? "✓ " : "✗ "}{toast.msg}
        </div>
      )}

      {/* Summary lines — tap to open drawer */}
      {clientReferrals.length > 0 && (
        <div
          onClick={() => openDrawer("referrals")}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "8px 12px", borderRadius: 8, cursor: "pointer",
            background: `${C.border}15`,
            border: `1px solid ${C.border}30`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            Referrals
            <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 700, color: C.accent }}>
              ({clientReferrals.length})
            </span>
          </span>
          <span style={{ fontSize: 14, color: C.muted }}>›</span>
        </div>
      )}
      {jobSales.length > 0 && (
        <div
          onClick={() => openDrawer("sales")}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "8px 12px", borderRadius: 8, cursor: "pointer",
            background: `${C.border}15`,
            border: `1px solid ${C.border}30`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            Sales
            <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 700, color: C.accent }}>
              ({jobSales.length})
            </span>
          </span>
          <span style={{ fontSize: 14, color: C.muted }}>›</span>
        </div>
      )}

      {pendingTotal > 0 && (
        <div style={{ textAlign: "right", fontSize: 12, color: C.green, fontWeight: 600, paddingRight: 4, marginTop: 2 }}>
          Pending referral total: {fmt(pendingTotal)}
        </div>
      )}

      {/* Full-screen drawer — rendered via portal to escape scroll-container clipping on iOS */}
      {drawerType && createPortal(
        <>
          <div
            onClick={closeDrawer}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
              zIndex: 2000, backdropFilter: "blur(2px)",
            }}
          />
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0,
            width: "min(520px, 100vw)",
            background: C.bg, borderLeft: `1px solid ${C.border}`,
            zIndex: 2001, display: "flex", flexDirection: "column",
          }}>
            {/* Drawer header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{drawerTitle}</div>
              <button
                onClick={closeDrawer}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 20, color: C.muted, lineHeight: 1, padding: "0 4px",
                }}
              >×</button>
            </div>

            {/* Search */}
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}20`, flexShrink: 0 }}>
              <input
                type="text"
                placeholder="Search by job #, client, or title…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ ...inputStyle, fontSize: 13, padding: "7px 12px", width: "100%", boxSizing: "border-box" }}
              />
            </div>

            {/* Scrollable list */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filteredList.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 13, color: C.dim, fontStyle: "italic" }}>
                  {q ? "No matches" : "None"}
                </div>
              ) : (
                filteredList.map(renderRow)
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
